import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { CRM_DELETE_LEAD, CRM_EDIT_LEAD, CRM_EXPORT_LEAD, CRM_VIEW_ALL_LEAD, CRM_VIEW_ASSIGNED_LEAD } from "src/constants";
import { CRM_ROUTES, getLeadColumns } from "src/pages/crm/constants";
import { AdminService, CRMService } from "src/services";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "src/utils";
import { handlePdfDownload, Listing } from "src/components/pdf";
import { CardLayout, DeleteModal, MTable, SelectField } from "src/components/v3";

import { AddEditLead } from "./AddEditLead.component";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const LeadList = ({
  companyId = null,
  leadFilter = null,
  totalCount = 0,
  setTotalCount = () => {},
  onSuccess = () => {},
  reloadData,
}) => {
  const [leads, setLeads] = useState([]);
  const [editLeadId, setEditLeadId] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
    dropdowns,
    setDropdowns,
  } = usePersistedTableState({
    tableId: "leads",
    defaults: { dropdowns: { priorityId: "", assigneeId: "", assignees: [] }, sortModel: [{ field: "updatedAt", sort: "desc" }] },
  });
  const selectedPriorityId = dropdowns.priorityId;
  const setSelectedPriorityId = (priorityId) => setDropdowns({ priorityId });
  const selectedAssigneeId = dropdowns.assigneeId;
  const selectedAssignee = dropdowns.assignees;
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    leadId: null,
  });

  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const hasEditLead = useHasPermission(CRM_EDIT_LEAD);
  const hasDeleteLead = useHasPermission(CRM_DELETE_LEAD);
  const hasExportLead = useHasPermission(CRM_EXPORT_LEAD);
  const canSeeAllLeads = useHasPermission(CRM_VIEW_ALL_LEAD);
  const canSeeAssignedLeads = useHasPermission(CRM_VIEW_ASSIGNED_LEAD);

  const fetchData = useCallback(
    async (filters) => {
      setLoading(true);
      const userAssigneeId = canSeeAllLeads ? selectedAssigneeId || null : canSeeAssignedLeads ? user.id : null;

      try {
        const { data } = await CRMService.getLeads({
          ...filters,
          assigneeIds: userAssigneeId,
          ...(companyId !== null && { companyId }),
        });
        const leadRows = data?.rows || [];

        const statusIds = Array.from(new Set(leadRows.map((item) => item?.statusId).filter(Boolean)));

        if (statusIds.length > 0) {
          const { data: statusesResponse } = await CRMService.getLeadStatuses({
            ids: statusIds.join(","),
            limit: statusIds.length,
            ...(companyId !== null && { companyId }),
          });
          setStatuses(statusesResponse?.rows);
        }

        setLeads(leadRows);
        setTotalCount(data?.count || 0);
      } catch (error) {
        console.error("fetchData error:", error);
        toast.error("Error fetching data");
      } finally {
        setLoading(false);
      }
    },
    [canSeeAllLeads, canSeeAssignedLeads, user.id, setTotalCount, companyId, selectedAssigneeId],
  );

  useEffect(() => {
    const fetchPriorities = async () => {
      try {
        const { data } = await CRMService.getLeadPriorities({ includeGlobal: true });
        const rows = Array.isArray(data) ? data : data?.rows || [];
        setPriorities(rows);
      } catch (error) {
        console.error("fetchPriorities error:", error);
        toast.error("Error fetching lead priorities");
      }
    };

    fetchPriorities();
  }, []);

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      statusId: leadFilter,
      priorityId: selectedPriorityId,
      contactName: searchText,
    });
  }, [fetchData, sortModel, paginationState, leadFilter, selectedPriorityId, searchText, reloadData]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Leads" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Leads",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handlePriorityFilterChange = (value) => {
    setSelectedPriorityId(value ?? "");
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearPriorityFilter = () => {
    setSelectedPriorityId("");
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleAssigneeFilterChange = (value) => {
    const selected = Array.isArray(value) ? value : [];
    setDropdowns({
      // Only what the picker needs to match and label an option: these are remembered in sessionStorage.
      assignees: selected.map(({ id, firstName, lastName }) => ({ id, firstName, lastName })),
      assigneeId: selected.map((u) => u.id).join(","),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const onViewClick = (lead) => {
    navigate(`${CRM_ROUTES.leads}/${lead.id}`);
  };

  const handleOpenModal = (lead) => {
    setEditLeadId(lead?.id);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = (lead) => setDeleteModal({ isOpen: true, leadId: lead?.id });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false, leadId: null });

  const handleDelete = async () => {
    try {
      await CRMService.deleteLead(deleteModal.leadId);
      closeDeleteModal();
      onSuccess?.();
      toast.success("Lead deleted successfully");
    } catch (error) {
      console.error("handleDelete error:", error);
      toast.error("Failed to delete lead");
    }
  };

  const handleActions = async (lead, actionType) => {
    switch (actionType) {
      case "edit":
        handleOpenModal(lead);
        break;
      case "delete":
        openDeleteModal(lead);
        break;
      default:
        break;
    }
  };

  const columnHeaders = getLeadColumns(handleActions, hasEditLead, hasDeleteLead);
  const priorityDropdownOptions = priorities.map((priority) => ({
    label: priority?.name,
    value: priority?.id,
  }));

  const dropdownFilters = [
    {
      key: "priorityId",
      label: "Priority",
      value: selectedPriorityId,
      onChange: handlePriorityFilterChange,
      onClear: handleClearPriorityFilter,
      options: priorityDropdownOptions,
      placeholder: "Select Priority",
      multiple: false,
      minWidth: 130,
    },
  ];

  if (canSeeAllLeads) {
    dropdownFilters.push({
      key: "assigneeId",
      label: "Assignee",
      value: selectedAssignee,
      minWidth: 180,
      render: () => (
        <SelectField
          limitTags={1}
          multiSelect
          label="Assignee"
          value={selectedAssignee}
          getData={AdminService.getUserList}
          getDataStaticFilters={{ type: "USER", active: true, ...(companyId !== null && { companyId }) }}
          optionValueFields={["firstName", "lastName"]}
          onChange={handleAssigneeFilterChange}
          style={{
            minWidth: 190,
            maxWidth: 240,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#fff",
              borderRadius: "10px",
              height: 40,
              paddingRight: "65px !important",
              "& fieldset": {
                borderRadius: "10px",
              },
            },
            "& .MuiTextField-root": {
              marginBottom: "0 !important",
            },
            "& .MuiAutocomplete-tag": {
              maxWidth: "100%",
            },
            "& .MuiChip-label": {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiAutocomplete-inputRoot": {
              flexWrap: "nowrap",
            },
            "& .MuiAutocomplete-input": {
              minWidth: 0,
            },
          }}
        />
      ),
    });
  }

  const rows = leads.map((lead, index) => {
    const status = statuses.find((s) => s.id === lead.statusId);
    const priority = priorities.find((p) => p.id === lead.priorityId);

    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: lead.id,
      contactName: lead.contactName,
      mobile: lead.contactPhone,
      priority: priority?.name || "",
      status: status?.name || "",
    };
  });

  return (
    <>
      <CardLayout>
        <MTable
          dropdownFilters={dropdownFilters}
          // Search data
          placeholder="Search leads"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          // Export Button data
          showExportButton={hasExportLead}
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>
      {/* Edit Lead modal*/}
      <AddEditLead leadId={editLeadId} open={openModal} onClose={handleCloseModal} onSuccess={onSuccess} />
      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this lead?"
      />
    </>
  );
};
